/**
 * Display helpers for the Notion meeting recordings list.
 *
 * Statuses come from the backend extraction ledger. A recording the connector
 * chose not to read is shown with its reason, never hidden. Notion is only ever
 * read for AI meeting notes; there is nothing to pick.
 */

const STATUS_LABELS = {
  pending: { label: "Waiting for first sync", tone: "muted" },
  extracted: { label: "Tasks found", tone: "ok" },
  unchanged: { label: "Up to date", tone: "ok" },
  not_ready: { label: "Notion is still processing", tone: "muted" },
  skipped_filter: { label: "Skipped", tone: "warn" },
  skipped_cap: { label: "Paused", tone: "warn" },
  too_large: { label: "Too large to read", tone: "warn" },
  inaccessible: { label: "Can't open recording", tone: "error" },
  error: { label: "Couldn't read recording", tone: "error" },
};

export const NOTION_MEETINGS_EXPLANATION = "Reads your Notion AI meeting notes.";

/**
 * The connector's lookback window as text, e.g. "last 7 days". Empty until the
 * backend has reported it.
 *
 * @example
 * windowLabel(7);
 * // "last 7 days"
 */
export const windowLabel = (days) => {
  const count = Number(days);
  if (!Number.isFinite(count) || count <= 0) return "";
  return count === 1 ? "last day" : `last ${count} days`;
};

/**
 * Label, tone, date and reason to show for one meeting recording.
 *
 * @example
 * describeNotionMeeting({ title: "Pipeline review", meeting_at: "2026-10-28T09:02:00+00:00", status: "not_ready", reason: "Notion is still processing this meeting." });
 * // { title: "Pipeline review", label: "Notion is still processing", tone: "muted", detail: "Notion is still processing this meeting.", dateLabel: "28 Oct" }
 */
export const describeNotionMeeting = (meeting) => {
  const status = STATUS_LABELS[String(meeting?.status || "")] || STATUS_LABELS.pending;
  return {
    title: String(meeting?.title || "").trim() || "Untitled meeting",
    label: status.label,
    tone: status.tone,
    detail: String(meeting?.reason || "").trim(),
    dateLabel: formatMeetingDate(meeting?.meeting_at),
  };
};

/**
 * Short local date for a meeting, or "" when it has none.
 *
 * @example
 * formatMeetingDate("2026-10-28T09:02:00+00:00");
 * // "28 Oct"
 */
export const formatMeetingDate = (value) => {
  if (!value) return "";
  const stamp = new Date(value);
  if (Number.isNaN(stamp.getTime())) return "";
  return stamp.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

/**
 * What to say about Notion itself when the last sync could not read the
 * recordings, or null when everything is fine (the list speaks for itself).
 *
 * @example
 * describeNotionStatus({ state: "upgrade_required" });
 * // { tone: "warn", text: "Your Notion plan doesn't include AI meeting notes. Upgrade to Notion Business with Notion AI to sync meetings." }
 */
export const describeNotionStatus = (status) => {
  const state = String(status?.state || "pending");
  const message = String(status?.message || "").trim();
  if (state === "upgrade_required" || state === "unavailable") {
    return {
      tone: "warn",
      text: "Your Notion plan doesn't include AI meeting notes. Upgrade to Notion Business with Notion AI to sync meetings.",
    };
  }
  if (state === "unreadable") {
    return {
      tone: "error",
      text: "Couldn't read Notion's meeting notes response. The next sync will try again.",
    };
  }
  if (state === "error") {
    return { tone: "error", text: message || "Notion meeting notes couldn't be read." };
  }
  return null;
};

/**
 * Empty-list text for a healthy connection.
 *
 * @example
 * emptyMeetingsText({ state: "pending" });
 * // "Waiting for the first sync."
 * emptyMeetingsText({ state: "ok" }, 7);
 * // "No meeting recordings in the last 7 days."
 */
export const emptyMeetingsText = (status, windowDays) => {
  if (String(status?.state || "pending") === "pending") return "Waiting for the first sync.";
  const label = windowLabel(windowDays);
  return label ? `No meeting recordings in the ${label}.` : "No meeting recordings in the sync window.";
};
